import { Component, Input, AfterViewInit, OnInit, ViewChild, Optional, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgModel, NgForm, AbstractControl } from '@angular/forms';
import { StateService, UrlService } from '@uirouter/angular';
import { TranslateModule } from '@ngx-translate/core';
import { BrandingAngularModule } from 'rev-portal/branding/BrandingAngular.Module';

import { AutoLinkModule } from 'rev-shared/ui/autoLink/AutoLink.Module';
import { ButtonsAngularModule } from 'rev-shared/ui/buttons/ButtonsAngular.Module';
import { CheckboxAngularModule } from 'rev-shared/ui/checkbox/CheckboxAngular.Module';
import { DateAngularModule } from 'rev-shared/date/DateAngular.Module';
import { DialogAngularModule } from 'rev-shared/ui/dialog/DialogAngular.Module';
import { DirectivesAngularModule } from 'rev-shared/util/directives/DirectivesAngular.Module';
import { FormDirectivesAngularModule } from 'rev-shared/util/directives/form/FormDirectivesAngular.Module';
import { FormGroupAngularModule } from 'rev-shared/ui/formGroup/FormGroupAngular.Module';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { PipesAngularModule } from 'rev-shared/pipes/Pipes.AngularModule';
import { SpinnerAngularModule } from 'rev-shared/ui/spinner/SpinnerAngular.Module';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { ValidationAngularModule } from 'rev-shared/ui/validation/ValidationAngular.Module';
import { ValidationModule } from 'rev-shared/util/directives/form/validation/Validation.AngularModule';
import { VbUiToggleContentDisplayAngularModule } from 'rev-shared/ui/toggleContentDisplay/VbUiTogleContentDisplay.AngularModule';
import { WebcastInfoViewComponent } from 'rev-shared/webcast/infoView/WebcastInfoView.Component';
import { isBeforeStartDate } from 'rev-shared/webcast/WebcastTimeCalculation';

import { WebcastService } from '../webcast/Webcast.Service';

import styles from './EventRegistration.module.less';

enum EventRegContext {
	PreReg = 'PreReg',
	Reg = 'Reg',
	Anonymous = 'Anomyous',
	AnonymousWithPassword = 'AnonymousPwd'
}

type ViewContext = Partial<{ [key in Partial<EventRegContext>]: boolean }>;

@Component({
	selector: 'event-registration',
	templateUrl: './EventRegistration.Component.html',
	imports: [
		AutoLinkModule,
		BrandingAngularModule,
		ButtonsAngularModule,
		CheckboxAngularModule,
		CommonModule,
		DateAngularModule,
		DialogAngularModule,
		DirectivesAngularModule,
		FormDirectivesAngularModule,
		FormGroupAngularModule,
		FormsModule,
		PipesAngularModule,
		SpinnerAngularModule,
		TranslateModule,
		ValidationAngularModule,
		ValidationModule,
		VbUiToggleContentDisplayAngularModule,
		WebcastInfoViewComponent,
	]
})
export class EventRegistrationComponent implements AfterViewInit, OnInit {
	@Input() public webcastInfo: any;
	@Input() public webcastUrl: string;
	@Input() public isPreview: boolean;
	@Input() public isOnPrem: boolean;
	@Input() public isRequireRegistrationEmailVerification: boolean;

	public readonly styles = styles;
	public status: { [key: string]: boolean } = { active: true };
	public error: string;

	public loginUrl: string;

	@ViewChild('form') public form: NgForm;
	@ViewChild('gdpr') public gdpr: NgModel;

	public viewContext: ViewContext;

	public contextSelector = {
		[EventRegContext.Anonymous]: webcastInfo => webcastInfo.allowAnonymousJoinMethod && !webcastInfo.isPasswordRequired,
		[EventRegContext.AnonymousWithPassword]: webcastInfo => webcastInfo.allowAnonymousJoinMethod && webcastInfo.isPasswordRequired,
		[EventRegContext.PreReg]: _webcastInfo => this.isInPreRegistration,
		[EventRegContext.Reg]: webcastInfo => !webcastInfo.allowAnonymousJoinMethod && !this.isInPreRegistration,
	};

	public formData = {
		name: null,
		email: null,
		password: null,
		gdprConsent: false
	};

	constructor(
		protected readonly $state: StateService,
		private readonly $url: UrlService,
		private readonly LoginRedirectService: LoginRedirectService,
		private readonly WebcastService: WebcastService,
		private readonly UserContext: UserContextService,

		@Optional() @Inject('afterSubmitFn')
		private readonly afterSubmitFn: () => void
	) {}

	public ngOnInit(): void {
		this.loginUrl = this.LoginRedirectService.getLoginLink(this.webcastUrl);

		this.setViewContext();

		if (this.viewContext.Anomyous) {
			this.submit();
		}
	}

	public ngAfterViewInit(): void {
		this.gdpr?.control.setValidators(({ value })=> !value ? { gdpr: true } : undefined);
	}

	public submit(skipPreRegistration?: boolean): void {
		if(this.isPreview){
			return;
		}

		this.status = { processing: true };
		this.error = null;
		const promise = this.webcastInfo.allowAnonymousJoinMethod ? this.registerAnonymousGuest() : this.registerGuest(skipPreRegistration);

		promise
			.then(user => {
				this.onRegisterGuestSuccess(user);
			})
			.catch(err => {
				console.error('error registering guest', err);
				if (err?.error?.attendeeRemoved) {
					this.error = 'AttendeeRemoved';
				} else if (err?.error?.guestAlreadyRegistered) {
					this.error = 'GuestAlreadyRegistered';
				}
				else {
					this.error = 'Unknown';
				}
				this.status = { active: true };
			});
	}

	private onRegisterGuestSuccess(user: any): void {
		const alreadyRegistered = this.UserContext.isRegisteredGuest();

		if(!alreadyRegistered) {
			//If user registered already, don't  update userContext.
			this.UserContext.registerGuestUser(user);
		}
		if (!this.webcastInfo.allowAnonymousJoinMethod) {
			if ((!this.isOnPrem && (this.isRequireRegistrationEmailVerification || this.viewContext.PreReg)) || alreadyRegistered) {
				this.status = { completed: true };
				return;
			}
		}

		this.afterSubmit();
	}

	protected afterSubmit(): void {
		if(this.afterSubmitFn) {
			this.afterSubmitFn();
			return;
		}

		const path = location.pathname || '/';
		window.history.replaceState({}, '', `${path}#` + this.webcastUrl);
		this.$url.sync();
	}

	public showFieldError(ctrl: AbstractControl): boolean {
		return ctrl?.touched && ctrl.invalid;
	}

	private get isInPreRegistration(): boolean {
		return this.webcastInfo.isPublic
			&& !this.webcastInfo.allowAnonymousJoinMethod
			&& isBeforeStartDate(this.webcastInfo.startDate, this.webcastInfo.lobbyTimeMinutes);
	}

	private registerGuest(skipPreRegistration: boolean): Promise<any> {
		const user = this.UserContext.getUser();
		return this.WebcastService.registerGuestUser(this.webcastInfo.webcastId, {
			name: this.formData.name,
			email: this.formData.email,
			password: this.formData.password,
			registrationFieldsAnswers: (this.webcastInfo.registrationFields || [])
				.filter(field => !!field.value)
				.map(field => ({ id: field.id, name: field.name, value: field.value })),
			skipPreRegistration,
			disableSessionCookie: user.isRegisteredGuest
		});
	}

	private registerAnonymousGuest(): Promise<any> {
		return this.WebcastService.registerAnonymousGuestUser(this.webcastInfo.webcastId, {
			password: this.formData.password
		});
	}

	public setViewContext(): void {
		this.viewContext = Object.entries(this.contextSelector)
			.reduce((output, current) => {
				const [key, value] = current;
				if (value(this.webcastInfo))
				{
					output[key] = true;
				}
				return output;
			}, {});
	}
}
